/**
 * FollowUpExtension — TipTap extension that provides:
 * 1. ProseMirror decorations: highlights paragraphs containing follow-up mentions
 *    and adds inline undo buttons.
 * 2. Text-change tracking: when a follow-up paragraph is edited, calls back with
 *    the new text so the task title can be updated.
 */

import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Node as PmNode } from '@tiptap/pm/model';

export interface FollowUpExtensionOptions {
  onUndo?: (taskId: string) => void;
  onTextChanged?: (taskId: string, newText: string) => void;
}

const followUpPluginKey = new PluginKey('followUpDecorations');

/**
 * Scan a paragraph node for mention children with followUpTaskId.
 * Returns list of { taskId, mentionPos } found.
 */
function findFollowUpMentions(node: PmNode, nodePos: number) {
  const results: { taskId: string; mentionPos: number }[] = [];
  node.forEach((child, offset) => {
    if (child.type.name === 'mention' && child.attrs.followUpTaskId) {
      results.push({
        taskId: child.attrs.followUpTaskId,
        mentionPos: nodePos + 1 + offset, // +1 for paragraph open
      });
    }
  });
  return results;
}

/**
 * Get plain text of a paragraph node.
 */
function paragraphText(node: PmNode): string {
  let text = '';
  node.forEach((child) => {
    if (child.isText) {
      text += child.text;
    } else if (child.type.name === 'mention') {
      const char = child.attrs.mentionSuggestionChar || '@';
      text += `${char}${child.attrs.label ?? child.attrs.id}`;
    }
  });
  return text.trim();
}

export function createFollowUpExtension(options?: FollowUpExtensionOptions): Extension {
  // Cache for paragraph text to detect changes
  const textCache = new Map<string, string>();
  let debounceTimers = new Map<string, ReturnType<typeof setTimeout>>();

  return Extension.create({
    name: 'followUpExtension',

    addProseMirrorPlugins() {
      return [
        new Plugin({
          key: followUpPluginKey,

          state: {
            init(_, state) {
              return buildDecorations(state.doc);
            },
            apply(tr, oldDecorations) {
              if (!tr.docChanged) return oldDecorations;
              const newDecorations = buildDecorations(tr.doc);

              // Check for text changes in follow-up paragraphs
              if (options?.onTextChanged) {
                tr.doc.descendants((node, pos) => {
                  if (node.type.name !== 'paragraph') return;
                  const mentions = findFollowUpMentions(node, pos);
                  if (mentions.length === 0) return;

                  const text = paragraphText(node);
                  for (const { taskId } of mentions) {
                    const cached = textCache.get(taskId);
                    if (cached !== undefined && cached !== text) {
                      // Text changed — debounce the callback
                      if (debounceTimers.has(taskId)) {
                        clearTimeout(debounceTimers.get(taskId)!);
                      }
                      debounceTimers.set(taskId, setTimeout(() => {
                        options.onTextChanged!(taskId, text);
                        debounceTimers.delete(taskId);
                      }, 1000));
                    }
                    textCache.set(taskId, text);
                  }
                });
              }

              return newDecorations;
            },
          },

          props: {
            decorations(state) {
              return followUpPluginKey.getState(state);
            },
          },
        }),
      ];

      function buildDecorations(doc: PmNode): DecorationSet {
        const decorations: Decoration[] = [];

        doc.descendants((node, pos) => {
          if (node.type.name !== 'paragraph') return;

          const mentions = findFollowUpMentions(node, pos);
          if (mentions.length === 0) return;

          // Add paragraph decoration (CSS class)
          decorations.push(
            Decoration.node(pos, pos + node.nodeSize, {
              class: 'follow-up-paragraph',
            }),
          );

          // Add undo widget at end of paragraph
          const endPos = pos + node.nodeSize - 1;
          for (const { taskId } of mentions) {
            // Initialize text cache
            if (!textCache.has(taskId)) {
              textCache.set(taskId, paragraphText(node));
            }

            decorations.push(
              Decoration.widget(endPos, () => {
                const btn = document.createElement('span');
                btn.className = 'follow-up-undo-btn';
                btn.title = 'Remove follow-up';
                btn.textContent = '✕';
                btn.addEventListener('mousedown', (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  options?.onUndo?.(taskId);
                });
                return btn;
              }, { side: 1, key: `follow-up-undo-${taskId}` }),
            );
          }
        });

        return DecorationSet.create(doc, decorations);
      }
    },
  });
}
